import { Skeleton } from '@/components/_ui/Skeleton'
import T, { useTranslate } from '@/components/Helpers/Translate'
import type { HydraGetShipToOption } from '@/lib/services/Hydra/shipToOptions/hydraGetShipToOptions'
import type { HydraShipByEstimationResponse } from '@/lib/services/Hydra/shipToOptions/hydraShipByEstimation'
import { convertToDate } from '@/lib/utils/shared/convertToDate'
import { getRelativeTimeFormat } from '@/lib/utils/shared/getRelativeTimeFormat'
import useGlobalStore from '@/store/useGlobalStore'
import Image from 'next/image'
import { useCallback } from 'react'

type Props = {
  isLoadingShipByEstimation: boolean
  isLoadingShipToOptions: boolean
  shipToOptions: HydraGetShipToOption[] | null
  shipByEstimation: HydraShipByEstimationResponse | null | undefined
}

export const ShipByEstimation = ({
  isLoadingShipByEstimation,
  isLoadingShipToOptions,
  shipByEstimation,
  shipToOptions,
}: Props) => {
  const {
    app: { language, alpha2, marketExceptions },
    user: { isSignedIn },
  } = useGlobalStore()

  const { translate } = useTranslate()

  const renderShipByDate = useCallback(() => {
    if (!marketExceptions?.hasShipByDate) return null

    if (!shipByEstimation || !shipByEstimation.shipBy) {
      return null
    }

    const tomorrowCalc = new Date()
    tomorrowCalc.setDate(tomorrowCalc.getDate() + 1)

    const shipByDate = convertToDate({
      dateTime: shipByEstimation.shipBy,
      language,
      alpha2,
    })

    const tomorrow = convertToDate({
      dateTime: tomorrowCalc.toUTCString(),
      language,
      alpha2,
    })

    const today = convertToDate({
      dateTime: new Date().toUTCString(),
      language,
      alpha2,
    })

    if (shipByDate === today || shipByDate === tomorrow) {
      return (
        <div className="flex flex-row">
          <span className="pr-1">
            {getRelativeTimeFormat({
              value: shipByDate === today ? 0 : 1,
              unit: 'day',
              language,
              alpha2,
            })}{' '}
          </span>
        </div>
      )
    }

    return (
      <div className="flex flex-row">
        <span className="pr-1">{shipByDate} </span>
      </div>
    )
  }, [shipByEstimation, language, alpha2])

  const renderShipByEstimation = () => {
    if (isLoadingShipByEstimation || isLoadingShipToOptions) {
      return (
        <Skeleton className="align-center flex flex-row">
          <Image
            className="mr-5 h-3.5"
            alt={translate('ships_by')}
            src="/icons/truck.svg"
            width={15}
            height={15}
          />

          <div className="flex flex-col md:flex-row">
            <span className="pr-1 font-medium">
              <T>ships_by</T>
            </span>
          </div>
        </Skeleton>
      )
    }

    if (!isSignedIn) {
      return (
        <div className="align-center flex flex-row">
          <Image
            className="mr-5 h-3.5"
            alt={translate('ships_by')}
            src="/icons/truck.svg"
            width={15}
            height={15}
          />

          <div className="flex flex-col md:flex-row">
            <span className="pr-1 font-medium">
              <T>shipby_login</T>
            </span>
          </div>
        </div>
      )
    }

    if (!shipToOptions || shipToOptions.length === 0) {
      return (
        <div className="align-center flex flex-row">
          <Image
            className="mr-5 h-3.5"
            alt={translate('ships_by')}
            src="/icons/truck.svg"
            width={15}
            height={15}
          />

          <div className="flex flex-col md:flex-row">
            <span className="pr-1 font-medium">
              <T>shipby_no_address</T>
            </span>
          </div>
        </div>
      )
    }

    return (
      <div className="align-center flex flex-row">
        <Image
          className="mr-5 h-3.5"
          alt={translate('ships_by')}
          src="/icons/truck.svg"
          width={15}
          height={15}
        />

        <div className="flex flex-col md:flex-row">
          {!shipByEstimation || !shipByEstimation.shipBy ? (
            <T>delayed_shipping</T>
          ) : (
            <>
              <span className="pr-1 font-medium">
                <T>ships_by</T>
              </span>
              {renderShipByDate()}
            </>
          )}
        </div>
      </div>
    )
  }

  return <div className="px-5 py-2">{renderShipByEstimation()}</div>
}
